import { FC, useEffect, useRef, useState } from 'react';

import { useCms, useContent } from '@eneco/cms';
import type { Image } from '@eneco/cms-types';
import type { InspirationAdviceFilterContent } from '@eneco/cms-types/generated/InspirationAdviceFilter';
import { AnimateHeightBox } from '@eneco/custom-components/AnimateHeightBox';
import { useTranslation } from '@eneco/i18n';
import { Box, Button, TextLink, Grid, PageGrid, Stack, Text, Stretch } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { SpinnerIcon } from '@eneco/sparky/icons';

import { AdviceCard } from './components/AdviceCard/AdviceCard';
import { FilterSentence } from './components/FilterSentence/FilterSentence';
import { TipCard } from './components/TipCard/TipCard';
import { useFilter } from './InspirationAdviceFilter.context';
import { type AdviceFilterCard, itemKey } from './types/inspirationAdviceFilter.types';
import { filterAdviceItemsObject } from './utils/filterAdviceItemsObject';

const cardIterationCountDesktop = 9;
const cardIterationCountMobile = 4;

/**
 * The card showed a focal point image's `default` size. For the serialisation that keys its sizes by name, the
 * Sitecore adapter takes `small` first, so `default` is preferred where the image has one.
 */
const withDefaultSize = (raw: unknown, image: Image | undefined): Image | undefined => {
  const defaultSrc = (raw as { default?: { src?: unknown } } | undefined)?.default?.src;
  return image && typeof defaultSrc === 'string' ? { ...image, src: defaultSrc } : image;
};

/** On Sanity an advice card without an image has no `focalImage` at all; its type still says what it is. */
const isAdviceCard = (card: AdviceFilterCard) =>
  ('focalImage' in card && card.focalImage !== undefined) || card._type === 'InspirationAdviceFilterAdviceCard';

export const InspirationAdviceFilterView: FC = () => {
  const isDesktop = useMediaQuery('md');

  const { adapter } = useCms();
  const content = useContent<InspirationAdviceFilterContent>();
  // An entry without an item name is a reference the migration could not resolve.
  const cardsList = ((content.cardsList ?? []) as AdviceFilterCard[]).filter(card => card.itemName);
  const { t } = useTranslation();
  const { filterTags } = useFilter();
  const [isAnimating, setIsAnimating] = useState(false);
  const [cardsGridHeight, setCardsGridHeight] = useState<number | undefined>();
  const [cardIterationCount, setCardIterationCount] = useState(cardIterationCountDesktop);
  const [visibleItemsCount, setVisibleItemsCount] = useState(cardIterationCount);

  const inspirationAdviceContainer = useRef<HTMLDivElement>(null);
  const adviceCardGrid = useRef<HTMLDivElement>(null);
  const prevVisibleItemsCount = useRef(visibleItemsCount);
  const firstNewResultRef = useRef<HTMLLIElement>(null);

  const { filteredAdviceCards, maxAmtOfCards } = filterAdviceItemsObject(cardsList, filterTags, visibleItemsCount);
  const allAdviceItemsAreVisible = visibleItemsCount >= maxAmtOfCards;

  const handleButtonClick = () => {
    const updatedVisibleItemsCount = Math.min(cardsList.length, visibleItemsCount + cardIterationCount);

    if (!allAdviceItemsAreVisible) {
      setIsAnimating(true);
      setVisibleItemsCount(updatedVisibleItemsCount);
      prevVisibleItemsCount.current = visibleItemsCount;

      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (visibleItemsCount && adviceCardGrid.current) {
      setCardsGridHeight(adviceCardGrid.current.clientHeight + 20); // Adding extra height to prevent cutoff card shadow
    }
  }, [visibleItemsCount]);

  useEffect(() => {
    setCardIterationCount(
      isDesktop === undefined || isDesktop ? cardIterationCountDesktop : cardIterationCountMobile,
    );
  }, [isDesktop]);

  useEffect(() => {
    setVisibleItemsCount(cardIterationCount);
  }, [cardIterationCount]);

  useEffect(() => {
    if (!isAnimating && firstNewResultRef.current) firstNewResultRef.current?.focus();
  }, [isAnimating]);

  const renderLoadMore = () => {
    if (isAnimating) {
      return (
        <Stack alignX="center">
          <SpinnerIcon color="iconSecondary" size="large" />
        </Stack>
      );
    }

    if (allAdviceItemsAreVisible) return null;

    return (
      <Stretch width={{ initial: true, md: false }}>
        <Button onClick={() => handleButtonClick()}>{t('generalLabels.loadMore')}</Button>
      </Stretch>
    );
  };

  return (
    <Box paddingBottom="12" backgroundColor="backgroundSecondary" ref={inspirationAdviceContainer}>
      <PageGrid>
        <PageGrid.Item gridColumn="1/-1">
          <FilterSentence />
        </PageGrid.Item>
        <PageGrid.Item gridColumn="1/-1">
          <AnimateHeightBox newHeight={cardsGridHeight} isAnimating={isAnimating}>
            <Grid
              as="ul"
              columns={{ initial: '1', md: '2' }}
              flow="dense"
              gap="10"
              gridTemplateColumns={{ xl: 'repeat(3, 1fr)' }}
              ref={adviceCardGrid}>
              {filteredAdviceCards.map((value, index) => {
                const cardIsLoading = index < prevVisibleItemsCount.current ? false : isAnimating;
                if (!value.title || !value.text) return null;

                return (
                  <li
                    ref={index === prevVisibleItemsCount.current ? firstNewResultRef : undefined}
                    key={itemKey(value)}
                    tabIndex={-1}>
                    {isAdviceCard(value) ? (
                      <Grid.Item as="div">
                        <AdviceCard
                          heading={value.title}
                          link={value.link}
                          image={
                            'focalImage' in value
                              ? withDefaultSize(value.focalImage, adapter.toImage(value.focalImage))
                              : undefined
                          }
                          isLoading={cardIsLoading}
                          text={value.text}
                        />
                      </Grid.Item>
                    ) : (
                      <Grid.Item as="div">
                        <TipCard heading={value.title} link={value.link} isLoading={cardIsLoading} text={value.text} />
                      </Grid.Item>
                    )}
                  </li>
                );
              })}
            </Grid>
          </AnimateHeightBox>
          <Box paddingTop="12">
            <Stack alignX="center" gap="3">
              <Text weight="bold" color="textLowEmphasis">
                {filteredAdviceCards.length} van {maxAmtOfCards}
              </Text>
              {renderLoadMore()}
              {visibleItemsCount !== cardIterationCount ? (
                <TextLink
                  onClick={() =>
                    window.scrollTo({
                      top: inspirationAdviceContainer.current?.offsetTop,
                      behavior: 'smooth',
                    })
                  }>
                  {t('generalLabels.toTop')}
                </TextLink>
              ) : null}
            </Stack>
          </Box>
        </PageGrid.Item>
      </PageGrid>
    </Box>
  );
};
